// === Admin: Fertilizers ===
// Full CRUD — list, add, edit, soft-delete.
// Ported from Click2Gro PHP (FertilzerController.php / fertilzers table).
// Note: "fertilzers" / "fertilzer" typo is preserved from the original PHP DB.
// Columns: fertilzer (product name), symbol, category, fertilzer_company,
//          cost_unit_default, safety_data_sheets, safety_data_sheet_link, plant_nutrients.

import { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Pagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  Chip,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ArrowBack as BackIcon,
  Description as DescriptionIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { alpha, darken } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useSdsDocuments, SdsDocumentViewerDialog } from '@click2gro/ui';
import { useAuth } from '../lib/auth';
import {
  AppTableHead,
  AppTableHeadCell,
  AppTableRow,
  ROWS_PER_PAGE_OPTIONS,
} from '../components/AppTable';
import {
  CatalogueCountrySwitch,
  catalogueCountryName,
  useCatalogueCountry,
  toCatalogueCountry,
  catalogueCurrency,
  type CatalogueCountry,
} from '../components/CatalogueCountrySwitch';
import { useCountries } from '../lib/countries';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PlantNutrient {
  readonly id: number;
  readonly element: string;
  readonly symbol: string;
  readonly order: number;
  readonly background_color: string;
  readonly text_color: string;
}
interface CategoryOption { readonly id: number; readonly category: string; }
interface CompanyOption  { readonly id: number; readonly company: string; }
interface SdsOption      { readonly id: number; readonly product: string | null; }

type LinkStatus = 'active' | 'issue' | null;

interface FertilizerRow {
  readonly id: number;
  readonly fertilzer: string | null;
  readonly symbol: string | null;
  readonly category: number | null;
  readonly fertilzer_company: number | null;
  readonly cost_unit_default: number | null;
  readonly safety_data_sheets: number | null;
  readonly safety_data_sheet_link: string | null;
  readonly safety_data_sheet_link_url: string | null;
  readonly plant_nutrients: Record<string, string> | null;
  readonly country_code?: string | null;
  readonly sds_link_status: LinkStatus;
  readonly sds_link_last_checked_at: string | null;
  readonly sds_link_status_message: string | null;
  readonly sds_link_replacement_url: string | null;
  readonly created_at: string | null;
  readonly created_by: string | null;
  readonly updated_at: string | null;
  readonly updated_by: string | null;
  readonly categories?: { readonly id: number; readonly category: string } | null;
  readonly fertilzer_companies?: { readonly id: number; readonly company: string } | null;
}

interface FormState {
  fertilzer: string;
  symbol: string;
  category: number | '';
  fertilzer_company: number | '';
  cost_unit_default: string;
  safety_data_sheets: number | '';
  safety_data_sheet_link: string;
  // Resolved signed URL from the safety_data_sheets FK (single source of truth).
  safety_data_sheet_link_url: string | null;
  plant_nutrients: Record<string, string>;
  // link-check status (edit mode only)
  sds_link_status: LinkStatus;
  sds_link_status_message: string | null;
  sds_link_replacement_url: string | null;
  sds_link_last_checked_at: string | null;
  country_code: CatalogueCountry;
}

const DEFAULT_FORM: FormState = {
  fertilzer: '',
  symbol: '',
  category: '',
  fertilzer_company: '',
  cost_unit_default: '',
  safety_data_sheets: '',
  safety_data_sheet_link: '',
  safety_data_sheet_link_url: null,
  plant_nutrients: {},
  sds_link_status: null,
  sds_link_status_message: null,
  sds_link_replacement_url: null,
  sds_link_last_checked_at: null,
  country_code: 'AU',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function getApiUrl(): string {
  return (import.meta.env['VITE_API_URL'] as string | undefined) ?? 'http://localhost:8787';
}
function authHeaders(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}
function fmtAuDate(value: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat('en-AU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(d);
}

// ---------------------------------------------------------------------------
// LinkStatusChip — list-view "Link check" cell
// ---------------------------------------------------------------------------

interface LinkStatusChipProps {
  status: LinkStatus;
  url: string | null;
  message: string | null;
}

function LinkStatusChip({ status, url, message }: LinkStatusChipProps) {
  const isExternal = Boolean(url) && (url!.startsWith('http://') || url!.startsWith('https://'));
  // Bucket-stored files don't rot — render nothing.
  if (!isExternal) return null;
  if (status === 'active') {
    return <Chip label="Active" size="small" color="success" sx={{ fontSize: 11 }} />;
  }
  if (status === 'issue') {
    return (
      <Tooltip title={message ?? 'Broken link — see edit dialog for details'}>
        <Chip label="Issue" size="small" color="error" sx={{ fontSize: 11 }} />
      </Tooltip>
    );
  }
  return <Chip label="Pending" size="small" variant="outlined" sx={{ fontSize: 11 }} />;
}

// ---------------------------------------------------------------------------
// LinkCheckBanner — edit-dialog warning + Accept/Reject/Recheck actions
// ---------------------------------------------------------------------------

interface LinkCheckBannerProps {
  status: LinkStatus;
  message: string | null;
  replacementUrl: string | null;
  lastCheckedAt: string | null;
  onAccept: () => Promise<void> | void;
  onReject: () => Promise<void> | void;
  onRecheck: () => Promise<void> | void;
  busy: boolean;
}

function LinkCheckBanner({
  status, message, replacementUrl, lastCheckedAt,
  onAccept, onReject, onRecheck, busy,
}: LinkCheckBannerProps) {
  if (status !== 'issue') return null;
  return (
    <Alert
      severity="warning"
      sx={{ mb: 1 }}
      action={
        <Button size="small" color="inherit" onClick={() => void onRecheck()} disabled={busy}>
          Recheck
        </Button>
      }
    >
      <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
        Link check failed: {message ?? 'unknown reason'}
        {lastCheckedAt && (
          <Typography component="span" variant="caption" sx={{ color: 'text.secondary', fontWeight: 400, ml: 1 }}>
            (last checked {fmtAuDate(lastCheckedAt)})
          </Typography>
        )}
      </Typography>
      {replacementUrl ? (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
            Suggested replacement:&nbsp;
            <a href={replacementUrl} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>
              {replacementUrl}
            </a>
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            <Button size="small" variant="contained" color="success" onClick={() => void onAccept()} disabled={busy}>
              Accept replacement
            </Button>
            <Button size="small" variant="outlined" color="inherit" onClick={() => void onReject()} disabled={busy}>
              Reject
            </Button>
          </Box>
        </Box>
      ) : (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
          No replacement suggestion was found. Paste a new URL and Save, or click Recheck.
        </Typography>
      )}
    </Alert>
  );
}

// ---------------------------------------------------------------------------
// PlantNutrientsGrid — same colour-coded grid as chemicals page
// ---------------------------------------------------------------------------
interface PlantNutrientsGridProps {
  nutrients: readonly PlantNutrient[];
  values: Record<string, string>;
  onChange: (updated: Record<string, string>) => void;
}

function PlantNutrientsGrid({ nutrients, values, onChange }: PlantNutrientsGridProps) {
  if (nutrients.length === 0) return null;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
        Plant Nutrients (%)
      </Typography>
      <Grid container spacing={1}>
        {nutrients.map((n) => (
          <Grid key={n.id} item xs={6} sm={4} md={3}>
            <Box
              sx={{
                borderRadius: 1,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box
                sx={{
                  px: 1,
                  py: 0.5,
                  bgcolor: n.background_color,
                  color: n.text_color,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="caption" fontWeight={700}>{n.symbol}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.85, fontSize: '0.65rem' }}>{n.element}</Typography>
              </Box>
              <TextField
                size="small"
                fullWidth
                type="number"
                placeholder="0"
                value={values[String(n.id)] ?? ''}
                onChange={(e) => onChange({ ...values, [String(n.id)]: e.target.value })}
                inputProps={{ step: '0.001', min: '0', max: '100' }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 }, '& fieldset': { border: 'none' } }}
              />
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function FertilizersPage() {
  const { viewer, openViewer, closeViewer } = useSdsDocuments();
  const navigate    = useNavigate();
  const { session } = useAuth();
  const token       = session?.access_token ?? '';
  const [country, setCountry] = useCatalogueCountry();
  const countries = useCountries();

  // ---- Lookup data ----
  const [categories,     setCategories]     = useState<readonly CategoryOption[]>([]);
  const [fertCompanies,  setFertCompanies]  = useState<readonly CompanyOption[]>([]);
  const [plantNutrients, setPlantNutrients] = useState<readonly PlantNutrient[]>([]);
  const [sdsOptions,     setSdsOptions]     = useState<readonly SdsOption[]>([]);

  useEffect(() => {
    if (!token) return;
    void fetch(`${getApiUrl()}/api/v1/admin/fertilizers/lookup-data?country=${country}`, { headers: authHeaders(token) })
      .then((r) => r.json() as Promise<{
        categories: CategoryOption[];
        fertilzerCompanies: CompanyOption[];
        plantNutrients: PlantNutrient[];
        safetyDataSheets: SdsOption[];
      }>)
      .then((d) => {
        setCategories(d.categories ?? []);
        setFertCompanies(d.fertilzerCompanies ?? []);
        setPlantNutrients(d.plantNutrients ?? []);
        setSdsOptions(d.safetyDataSheets ?? []);
      });
  }, [token, country]);

  // ---- List state ----
  const [rows,        setRows]        = useState<readonly FertilizerRow[]>([]);
  const [total,       setTotal]       = useState(0);
  const [page,        setPage]        = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search,      setSearch]      = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading,     setLoading]     = useState(false);
  const [listError,   setListError]   = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => { setPage(0); setDebouncedSearch(search); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  // ---- Dialog ----
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId,  setEditingId]  = useState<number | null>(null);
  const [auditInfo,  setAuditInfo]  = useState<{
    created_at: string | null; updated_at: string | null;
    created_by: string | null; updated_by: string | null;
  } | null>(null);
  const [form,       setForm]       = useState<FormState>(DEFAULT_FORM);
  const [formError,  setFormError]  = useState<string | null>(null);
  const [saving,     setSaving]     = useState(false);

  // ---- File Upload ----
  const [selectedFile,  setSelectedFile]   = useState<File | null>(null);
  const [uploadingFile, setUploadingFile]  = useState(false);
  const fileInputRef    = useRef<HTMLInputElement>(null);

  // ---- Delete ----
  const [deleteTarget, setDeleteTarget] = useState<FertilizerRow | null>(null);
  const [deleting,     setDeleting]     = useState(false);

  // ---- Link-check action busy flag ----
  const [linkActionBusy, setLinkActionBusy] = useState(false);

  // ---- Snackbar ----
  const [snack, setSnack] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);
  const showSnack = useCallback(
    (msg: string, severity: 'success' | 'error' = 'success') => setSnack({ msg, severity }),
    [],
  );

  // ---- Load list ----
  const loadList = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setListError(null);
    try {
      const params = new URLSearchParams({
        page:  String(page + 1),
        limit: String(rowsPerPage),
        country,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      });
      const res = await fetch(`${getApiUrl()}/api/v1/admin/fertilizers?${params.toString()}`, {
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as { total: number; data: FertilizerRow[] };
      setRows(json.data ?? []);
      setTotal(json.total ?? 0);
    } catch (e) {
      setListError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [token, page, rowsPerPage, debouncedSearch, country]);

  useEffect(() => { void loadList(); }, [loadList]);

  // ---- Open add ----
  const openAdd = () => {
    setEditingId(null);
    setAuditInfo(null);
    setForm({ ...DEFAULT_FORM, country_code: country });
    setFormError(null);
    setDialogOpen(true);
  };

  // ---- Open edit ----
  const openEdit = async (row: FertilizerRow) => {
    setEditingId(row.id);
    setAuditInfo({
      created_at: row.created_at, updated_at: row.updated_at,
      created_by: row.created_by, updated_by: row.updated_by,
    });
    setForm({
      fertilzer:              row.fertilzer               ?? '',
      symbol:                 row.symbol                  ?? '',
      category:               row.category                ?? '',
      fertilzer_company:      row.fertilzer_company       ?? '',
      cost_unit_default:      row.cost_unit_default != null ? String(row.cost_unit_default) : '',
      safety_data_sheets:     row.safety_data_sheets      ?? '',
      safety_data_sheet_link: row.safety_data_sheet_link  ?? '',
      safety_data_sheet_link_url: row.safety_data_sheet_link_url ?? null,
      plant_nutrients:        (row.plant_nutrients as Record<string, string>) ?? {},
      sds_link_status:           row.sds_link_status ?? null,
      sds_link_status_message:   row.sds_link_status_message ?? null,
      sds_link_replacement_url:  row.sds_link_replacement_url ?? null,
      sds_link_last_checked_at:  row.sds_link_last_checked_at ?? null,
      country_code:              toCatalogueCountry(row.country_code ?? country),
    });
    setFormError(null);
    setDialogOpen(true);
  };

  // ---- Upload file ----
  const uploadFile = async (file: File): Promise<string | null> => {
    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${getApiUrl()}/api/v1/admin/fertilizers/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      const result = (await res.json()) as { path?: string };
      return result.path ?? null;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Upload failed';
      setFormError(`File upload failed: ${msg}`);
      return null;
    } finally {
      setUploadingFile(false);
    }
  };

  // ---- Save ----
  const handleSave = async () => {
    if (!form.fertilzer.trim()) { setFormError('Product name is required'); return; }
    setSaving(true);
    setFormError(null);
    try {
      // Upload file if selected
      let linkPath = form.safety_data_sheet_link;
      if (selectedFile) {
        const uploadedPath = await uploadFile(selectedFile);
        if (!uploadedPath) return; // uploadFile sets error message
        linkPath = uploadedPath;
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }

      const url    = editingId
        ? `${getApiUrl()}/api/v1/admin/fertilizers/${editingId}`
        : `${getApiUrl()}/api/v1/admin/fertilizers`;
      const method = editingId ? 'PUT' : 'POST';
      const res    = await fetch(url, {
        method,
        headers: authHeaders(token),
        body: JSON.stringify({
          fertilzer:              form.fertilzer.trim() || null,
          symbol:                 form.symbol.trim() || null,
          category:               form.category || null,
          fertilzer_company:      form.fertilzer_company || null,
          cost_unit_default:      form.cost_unit_default || null,
          safety_data_sheets:     form.safety_data_sheets || null,
          safety_data_sheet_link: linkPath.trim() || null,
          plant_nutrients:        Object.keys(form.plant_nutrients).length > 0 ? form.plant_nutrients : null,
          country_code:           form.country_code,
        }),
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      setDialogOpen(false);
      showSnack(editingId ? 'Fertilizer updated' : 'Fertilizer added');
      void loadList();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ---- Link-check actions ----
  const reloadEditingRow = useCallback(async () => {
    if (!editingId || !token) return;
    const res = await fetch(`${getApiUrl()}/api/v1/admin/fertilizers/${editingId}`, {
      headers: authHeaders(token),
    });
    if (!res.ok) {
      showSnack(`Failed to reload: HTTP ${res.status}`, 'error');
      return;
    }
    const json = (await res.json()) as { data: FertilizerRow };
    const r = json.data;
    setForm((f) => ({
      ...f,
      safety_data_sheet_link:    r.safety_data_sheet_link ?? '',
      safety_data_sheet_link_url: r.safety_data_sheet_link_url ?? null,
      sds_link_status:           r.sds_link_status ?? null,
      sds_link_status_message:   r.sds_link_status_message ?? null,
      sds_link_replacement_url:  r.sds_link_replacement_url ?? null,
      sds_link_last_checked_at:  r.sds_link_last_checked_at ?? null,
    }));
  }, [editingId, token, showSnack]);

  const callLinkAction = useCallback(
    async (path: string, body: Record<string, unknown> = {}) => {
      if (!editingId || !token) return false;
      setLinkActionBusy(true);
      try {
        const res = await fetch(`${getApiUrl()}/api/v1/admin/fertilizers/${editingId}${path}`, {
          method: 'POST',
          headers: authHeaders(token),
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const err = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(err.error ?? `HTTP ${res.status}`);
        }
        return true;
      } catch (e) {
        showSnack(e instanceof Error ? e.message : 'Action failed', 'error');
        return false;
      } finally {
        setLinkActionBusy(false);
      }
    },
    [editingId, token, showSnack],
  );

  const handleAcceptReplacement = useCallback(async () => {
    const ok = await callLinkAction('/accept-replacement', { field: 'sds' });
    if (ok) {
      showSnack('Replacement link accepted.');
      await reloadEditingRow();
      void loadList();
    }
  }, [callLinkAction, showSnack, reloadEditingRow, loadList]);

  const handleRejectReplacement = useCallback(async () => {
    const ok = await callLinkAction('/reject-replacement', { field: 'sds' });
    if (ok) {
      showSnack('Replacement candidate dismissed.');
      await reloadEditingRow();
    }
  }, [callLinkAction, showSnack, reloadEditingRow]);

  const handleRecheckLinks = useCallback(async () => {
    const ok = await callLinkAction('/recheck-links');
    if (ok) {
      showSnack('Link re-checked.');
      await reloadEditingRow();
      void loadList();
    }
  }, [callLinkAction, showSnack, reloadEditingRow, loadList]);

  // ---- Delete ----
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/fertilizers/${deleteTarget.id}`, {
        method: 'DELETE',
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDeleteTarget(null);
      showSnack('Fertilizer deleted');
      void loadList();
    } catch {
      showSnack('Delete failed', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const totalPages = Math.ceil(total / rowsPerPage);

  // ---- Count filled nutrients ----
  const nutrientCount = Object.values(form.plant_nutrients).filter(Boolean).length;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/dashboard')} size="small">
          <BackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>Fertilizers</Typography>
          <Typography variant="body2" color="text.secondary">Fertilizer product library</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
          Add Fertilizer
        </Button>
      </Box>

      {/* Search + rows-per-page */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <CatalogueCountrySwitch value={country} onChange={(c) => { setCountry(c); setPage(0); }} />
          <TextField
            size="small"
            placeholder="Search fertilizers…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 200, maxWidth: 400 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <FormControl size="small" sx={{ minWidth: 80 }}>
            <Select value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}>
              {ROWS_PER_PAGE_OPTIONS.map((n) => (
                <MenuItem key={n} value={n}>{n}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            {total} record{total !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </Paper>

      {/* Table */}
      {listError && <Alert severity="error" sx={{ mb: 2 }}>{listError}</Alert>}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 800 }}>
            <AppTableHead>
              <TableRow>
                <AppTableHeadCell sx={{ minWidth: 200 }}>Product Name</AppTableHeadCell>
                <AppTableHeadCell sx={{ minWidth: 150 }}>Category</AppTableHeadCell>
                <AppTableHeadCell sx={{ minWidth: 180 }}>Company</AppTableHeadCell>
                <AppTableHeadCell sx={{ minWidth: 100 }}>Cost / Unit</AppTableHeadCell>
                <AppTableHeadCell sx={{ minWidth: 90 }}>Nutrients</AppTableHeadCell>
                <AppTableHeadCell sx={{ minWidth: 70 }}>Link</AppTableHeadCell>
                <AppTableHeadCell align="center" sx={{ minWidth: 110 }}>Link check</AppTableHeadCell>
                <AppTableHeadCell align="right" sx={{ minWidth: 90 }}>Actions</AppTableHeadCell>
              </TableRow>
            </AppTableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}><CircularProgress size={24} /></TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    No records found
                  </TableCell>
                </TableRow>
              ) : rows.map((row) => {
                const nutrients = row.plant_nutrients ?? {};
                const filledCount = Object.values(nutrients).filter(Boolean).length;
                return (
                  <AppTableRow key={row.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{row.fertilzer ?? '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.categories?.category ?? '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.fertilzer_companies?.company ?? '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {row.cost_unit_default != null ? `${catalogueCurrency(countries, country)}${Number(row.cost_unit_default).toFixed(2)}` : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {filledCount > 0 ? (
                        <Chip label={`${filledCount} set`} size="small" color="success" variant="outlined" />
                      ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                        {row.safety_data_sheet_link_url ? (
                          <Tooltip title="Safety Data Sheet">
                            <IconButton size="small" onClick={() => openViewer(row.safety_data_sheet_link_url, `${row.fertilzer} — Safety Data Sheet`)}>
                              <OpenInNewIcon fontSize="small" color="success" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <LinkStatusChip
                        status={row.sds_link_status}
                        url={row.safety_data_sheet_link}
                        message={row.sds_link_status_message}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => void openEdit(row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => setDeleteTarget(row)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </AppTableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {totalPages > 1 && (
          <Box sx={(theme) => ({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: theme.palette.mode === 'light'
              ? alpha(theme.palette.primary.main, 0.09)
              : darken(theme.palette.background.paper, 0.35),
            px: 2,
            py: 1.5,
          })}>
            <Typography variant="body2" sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85 })}>
              {loading ? 'Loading…' : total === 0 ? 'No records found'
                : `Showing ${page * rowsPerPage + 1} to ${Math.min((page + 1) * rowsPerPage, total)} of ${total} entries`}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85, whiteSpace: 'nowrap' })}>Show</Typography>
                <Select
                  size="small"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                  sx={(theme) => ({
                    color: theme.palette.text.primary,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
                    '& .MuiSvgIcon-root': { color: theme.palette.text.primary },
                    '& .MuiSelect-select': { py: 0.5, px: 1 },
                  })}
                >
                  {ROWS_PER_PAGE_OPTIONS.map((n) => (
                    <MenuItem key={n} value={n}>{n}</MenuItem>
                  ))}
                </Select>
              </Box>
              <Typography variant="body2" sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85, whiteSpace: 'nowrap' })}>
                Page {page + 1} of {totalPages}
              </Typography>
              <Pagination
                count={totalPages}
                page={page + 1}
                onChange={(_, v) => setPage(v - 1)}
                color="primary"
                size="small"
                disabled={loading}
                sx={(theme) => ({
                  '& .MuiPaginationItem-root': {
                    color: theme.palette.text.primary,
                    borderColor: theme.palette.divider,
                    '&:hover': { backgroundColor: theme.palette.action.hover },
                  },
                  '& .Mui-selected': {
                    backgroundColor: `${theme.palette.primary.main} !important`,
                    color: theme.palette.primary.contrastText,
                  },
                  '& .Mui-disabled': { opacity: 0.5 },
                })}
              />
            </Box>
          </Box>
        )}
      </Paper>

      {/* ------------------------------------------------------------------ */}
      {/* Add / Edit Dialog                                                   */}
      {/* ------------------------------------------------------------------ */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingId ? 'Edit Fertilizer' : 'Add Fertilizer'}
          {form.country_code !== 'AU' && (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', fontWeight: 600 }}>
              {catalogueCountryName(countries, form.country_code)} catalogue
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

            {/* Row 1: name + symbol */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 2 }}>
              <TextField
                label="Product Name *"
                size="small"
                fullWidth
                value={form.fertilzer}
                onChange={(e) => setForm((f) => ({ ...f, fertilzer: e.target.value }))}
              />
              <TextField
                label="Symbol"
                size="small"
                fullWidth
                value={form.symbol}
                onChange={(e) => setForm((f) => ({ ...f, symbol: e.target.value }))}
              />
            </Box>

            {/* Row 2: category + company */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  label="Category"
                  value={form.category}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value as number | '' }))}
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {categories.map((c) => <MenuItem key={c.id} value={c.id}>{c.category}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>Fertilizer Company</InputLabel>
                <Select
                  label="Fertilizer Company"
                  value={form.fertilzer_company}
                  onChange={(e) => setForm((f) => ({ ...f, fertilzer_company: e.target.value as number | '' }))}
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {fertCompanies.map((fc) => <MenuItem key={fc.id} value={fc.id}>{fc.company}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>

            {/* Row 3: cost + SDS FK */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label={`Cost per Unit (${catalogueCurrency(countries, form.country_code)})`}
                size="small"
                fullWidth
                type="number"
                inputProps={{ step: '0.0001', min: '0' }}
                value={form.cost_unit_default}
                onChange={(e) => setForm((f) => ({ ...f, cost_unit_default: e.target.value }))}
              />
              <FormControl size="small" fullWidth>
                <InputLabel>Safety Data Sheet</InputLabel>
                <Select
                  label="Safety Data Sheet"
                  value={form.safety_data_sheets}
                  onChange={(e) => setForm((f) => ({ ...f, safety_data_sheets: e.target.value as number | '' }))}
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {sdsOptions.map((s) => <MenuItem key={s.id} value={s.id}>{s.product ?? `#${s.id}`}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>

            {/* Row 4: File upload */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Upload Safety Data Sheet File (PDF)
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingFile || saving}
                  startIcon={uploadingFile ? <CircularProgress size={16} /> : <DescriptionIcon />}
                >
                  {selectedFile ? `${selectedFile.name}` : 'Choose File…'}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  hidden
                  onChange={(e) => {
                    const file = e.currentTarget.files?.[0];
                    if (file) setSelectedFile(file);
                  }}
                />
                {selectedFile && (
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() => {
                      setSelectedFile(null);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Box>
              {selectedFile && (
                <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
                  ✓ File selected: {(selectedFile.size / 1024).toFixed(1)} KB
                </Typography>
              )}
            </Box>

            {/* Row 5: SDS link + link-check banner */}
            <Box>
              {editingId && (
                <LinkCheckBanner
                  status={form.sds_link_status}
                  message={form.sds_link_status_message}
                  replacementUrl={form.sds_link_replacement_url}
                  lastCheckedAt={form.sds_link_last_checked_at}
                  onAccept={handleAcceptReplacement}
                  onReject={handleRejectReplacement}
                  onRecheck={handleRecheckLinks}
                  busy={linkActionBusy}
                />
              )}
              <TextField
                label="Safety Data Sheet Link (URL)"
                size="small"
                fullWidth
                placeholder="https://…"
                value={form.safety_data_sheet_link}
                onChange={(e) => setForm((f) => ({ ...f, safety_data_sheet_link: e.target.value }))}
                InputProps={{
                  // Show View whenever a document resolves — either a manually
                  // entered external URL, or the FK-resolved SDS link (single
                  // source of truth) when this row's own column is empty.
                  endAdornment: (form.safety_data_sheet_link_url || form.safety_data_sheet_link.startsWith('http')) ? (
                    <InputAdornment position="end">
                      <Tooltip title="View document">
                        <IconButton
                          size="small"
                          onClick={() => openViewer(form.safety_data_sheet_link_url ?? form.safety_data_sheet_link, 'Safety Data Sheet')}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ) : undefined,
                }}
              />
            </Box>

            {/* Plant Nutrients */}
            <PlantNutrientsGrid
              nutrients={plantNutrients}
              values={form.plant_nutrients}
              onChange={(pn) => setForm((f) => ({ ...f, plant_nutrients: pn }))}
            />

            {/* Nutrient summary pill */}
            {nutrientCount > 0 && (
              <Box>
                <Chip
                  label={`${nutrientCount} nutrient${nutrientCount !== 1 ? 's' : ''} entered`}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              </Box>
            )}

            {/* Audit Trail */}
            {auditInfo && (
              <Box
                p={2}
                sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.12), borderRadius: 1 }}
              >
                <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1} sx={{ textTransform: 'uppercase', letterSpacing: 0.8, textAlign: 'center' }}>
                  Audit Trail
                </Typography>
                <Box sx={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.disabled" display="block">Created</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {fmtAuDate(auditInfo.created_at)}{auditInfo.created_by ? <> &mdash; <strong>{auditInfo.created_by}</strong></> : ''}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.disabled" display="block">Updated</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {fmtAuDate(auditInfo.updated_at)}{auditInfo.updated_by ? <> &mdash; <strong>{auditInfo.updated_by}</strong></> : ''}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setDialogOpen(false)} color="inherit">Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : undefined}
          >
            {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Add Fertilizer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Delete Confirm                                                       */}
      {/* ------------------------------------------------------------------ */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Fertilizer?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{deleteTarget?.fertilzer ?? 'this record'}</strong>?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setDeleteTarget(null)} color="inherit">Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleDelete()}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : undefined}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack(null)}
          severity={snack?.severity ?? 'success'}
          sx={(theme) => ({
            bgcolor: snack?.severity === 'error'
              ? darken(theme.palette.error.main, 0.2)
              : darken(theme.palette.success.main, 0.2),
            color: '#fff',
          })}
        >
          {snack?.msg}
        </Alert>
      </Snackbar>

      <SdsDocumentViewerDialog viewer={viewer} onClose={closeViewer} />
    </Container>
  );
}
